import React, { useEffect } from 'react';
import { Drawer, TextField, Box, Stack, Alert, Typography } from '@mui/material';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { Select } from '@shared/components/Select/Select';
import { useOfferConfig } from '../../contexts/OfferConfigContext';
import { useBulkCreateOffer } from '../../hooks/useOffers';
import type { BulkOfferPayload } from '../../types';
import { ContentCopy, InfoOutlined } from '@mui/icons-material';
import { DrawerContent, DrawerFooter, DrawerHeader } from '@shared/components/drawer';

interface BulkOfferDialogProps {
  open: boolean;
  onClose: () => void;
}

type BulkOfferFormData = Omit<BulkOfferPayload, 'game_id'>;

const validationSchema = yup
  .object({
    scope: yup.number().required('Scope is required'),
    offer_category: yup.number().required('Offer category is required'),
    currency: yup.number().required('Currency is required'),
    duration: yup.number().min(0, 'Duration must be at least 0'),
    quantity: yup.number().min(1, 'Quantity must be at least 1').required('Quantity is required'),
  })
  .required();

export const BulkOfferDrawer: React.FC<BulkOfferDialogProps> = ({ open, onClose }) => {
  const { scopeOptions, categoryOptions, currencyOptions } = useOfferConfig();
  const bulkCreateMutation = useBulkCreateOffer();

  const {
    control,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(validationSchema),
    defaultValues: {
      scope: null,
      offer_category: null,
      currency: null,
      duration: 0,
      quantity: 1,
    },
  });

  // Categories that should show the duration field
  const CATEGORIES_REQUIRE_DURATION: number[] = categoryOptions
    .filter((cat) => /\bPremium Account\b/i.test(cat.label))
    .map((cat) => cat.optionId)
    .filter(Boolean);

  const selectedCategory = watch('offer_category');
  const showDuration: boolean = !!selectedCategory && CATEGORIES_REQUIRE_DURATION.includes(selectedCategory);

  useEffect(() => {
    if (!showDuration) {
      setValue('duration', 0, { shouldValidate: true, shouldDirty: true });
    }
  }, [showDuration, setValue]);

  const handleClose = () => {
    reset();
    onClose();
  };

  const onSubmit = async (data: yup.InferType<typeof validationSchema>) => {
    try {
      await bulkCreateMutation.mutateAsync(data as BulkOfferFormData);
      handleClose();
    } catch (error) {
      // Error handled by mutation
      console.error('Bulk create offers failed:', error);
    }
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={handleClose}
      slotProps={{ paper: { sx: { width: { xs: '100%', md: 480 } } } }}
    >
      <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
        <DrawerHeader
          title="Create Bulk Offers"
          icon={<ContentCopy fontSize="small" color="primary" />}
          onClose={handleClose}
        />

        <form
          noValidate
          onSubmit={handleSubmit(onSubmit)}
          style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}
        >
          <DrawerContent>
            <Stack spacing={3}>
              {/* Info Note */}
              <Alert
                severity="info"
                icon={<InfoOutlined />}
                sx={{
                  borderRadius: '4px',
                  bgcolor: (t) => (t.palette.mode === 'light' ? 'info.lighter' : 'gray.dark'),
                  '& .MuiAlert-icon': { color: 'info.main' },
                }}
              >
                <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                  What does Bulk Create do?
                </Typography>
                <Typography variant="body2" sx={{ fontSize: '0.8125rem' }}>
                  This feature creates <strong>multiple identical offers</strong> with the same configuration you
                  specify below. The <strong>Quantity</strong> field determines how many duplicate offers will be
                  created. For example, if you set Quantity to 5, it will create 5 offers with the exact same platform,
                  category, currency, and duration settings.
                </Typography>
              </Alert>

              <Controller
                name="scope"
                control={control}
                render={({ field }) => (
                  <Select
                    {...field}
                    label="Platform"
                    options={scopeOptions}
                    error={errors.scope?.message}
                    required
                    useOptionId
                    placeholder="Select platform"
                  />
                )}
              />

              <Controller
                name="offer_category"
                control={control}
                render={({ field }) => (
                  <Select
                    {...field}
                    label="Offer Category"
                    options={categoryOptions}
                    error={errors.offer_category?.message}
                    required
                    useOptionId
                    placeholder="Select category"
                  />
                )}
              />

              <Controller
                name="currency"
                control={control}
                render={({ field }) => (
                  <Select
                    {...field}
                    label="Currency"
                    options={currencyOptions}
                    error={errors.currency?.message}
                    required
                    useOptionId
                    placeholder="Select currency"
                  />
                )}
              />

              {/* {showDuration && ( */}
              <Controller
                name="duration"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    value={field.value ?? ''}
                    type="number"
                    label="Duration (seconds)"
                    fullWidth
                    error={!!errors.duration}
                    helperText={errors.duration?.message}
                  />
                )}
              />
              {/* )} */}

              <Controller
                name="quantity"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    value={field.value ?? ''}
                    type="number"
                    label="Quantity"
                    fullWidth
                    required
                    error={!!errors.quantity}
                    helperText={errors.quantity?.message}
                  />
                )}
              />
            </Stack>
          </DrawerContent>

          <DrawerFooter
            primaryButton={{
              label: 'Create Offers',
              type: 'submit',
              loading: bulkCreateMutation.isPending,
            }}
          />
        </form>
      </Box>
    </Drawer>
  );
};

export default BulkOfferDrawer;
